import client from './inventory-request';
import { withStoreCreateBody, withStoreUpdateBody } from '../hooks/useStoreScope';
import { documentsToRows, rowsToDocuments } from '../utils/vendorEntity';

function vendorActionBody(opts = {}) {
  const body = {};
  const actor = opts.action_by || sessionStorage.getItem('USER_ACCESS_KEY');
  const actionBy = actor && actor !== 'null' && actor !== 'undefined' ? actor : null;
  if (actionBy) body.action_by = actionBy;
  if (opts.reason) body.reason = opts.reason;
  if (opts.remarks) body.remarks = opts.remarks;
  return body;
}

export function fromApiVendor(v = {}) {
  return {
    vendor_code: v.vendor_code ?? '',
    vendor_name: v.vendor_name ?? '',
    legal_name: v.legal_name ?? '',
    vendor_type: v.vendor_type ?? '',
    is_active: v.is_active ?? true,
    vendor_category: v.vendor_category ?? '',
    remarks: v.remarks ?? '',
    address: v.address_line1 ?? v.address ?? '',
    address_line2: v.address_line2 ?? '',
    city: v.city ?? '',
    state: v.state ?? '',
    country: v.country ?? 'India',
    pincode: v.postal_code ?? v.pincode ?? '',
    contact_person: v.contact_person ?? '',
    phone: v.contact_phone ?? v.phone ?? '',
    alternate_phone: v.alternate_phone ?? '',
    email: v.contact_email ?? v.email ?? '',
    gstin: v.gst_number ?? v.gstin ?? '',
    pan: v.pan_number ?? v.pan ?? '',
    drug_license_no: v.drug_license_number ?? v.drug_license_no ?? '',
    license_valid_till: v.drug_license_valid_until ?? v.license_valid_till ?? '',
    payment_terms: v.payment_terms ?? '',
    credit_period: v.credit_period_days ?? v.credit_period ?? '',
    rating: v.rating ?? '',
    bank_name: v.bank_name ?? '',
    bank_account: v.account_number ?? v.bank_account ?? '',
    bank_ifsc: v.ifsc_code ?? v.bank_ifsc ?? '',
    currency: v.currency ?? 'INR',
    documentRows: documentsToRows(v.documents),
  };
}

export function isVendorActive(v) {
  return v?.is_active !== false;
}

export function filterActiveVendors(list) {
  return (Array.isArray(list) ? list : []).filter(isVendorActive);
}

export function isPurchasableVendor(v) {
  return isVendorActive(v)
    && v?.approval_status === 'APPROVED'
    && v?.can_purchase !== false;
}

export function filterPurchasableVendors(list) {
  return (Array.isArray(list) ? list : []).filter(isPurchasableVendor);
}

export async function getPurchasableVendors(params = {}) {
  const data = await getVendors({
    ...params,
    approval_status: 'APPROVED',
    is_active: true,
  });
  const list = Array.isArray(data) ? data : (data?.items || []);
  return filterPurchasableVendors(list);
}

export function toVendorApiPayload(data, { isEdit = false, existingDocuments } = {}) {
  const documents = rowsToDocuments(data.documentRows, isEdit ? existingDocuments : {});

  const payload = {
    vendor_name: data.vendor_name?.trim(),
    vendor_type: data.vendor_type,
    legal_name: data.legal_name?.trim() || undefined,
    is_active: data.is_active ?? true,
    vendor_category: data.vendor_category?.trim() || undefined,
    remarks: data.remarks?.trim() || undefined,
    address_line1: data.address?.trim(),
    address_line2: data.address_line2?.trim() || undefined,
    city: data.city?.trim(),
    state: data.state?.trim(),
    country: data.country?.trim(),
    postal_code: data.pincode?.trim(),
    contact_person: data.contact_person?.trim(),
    contact_phone: data.phone?.trim(),
    contact_email: data.email?.trim(),
    alternate_phone: data.alternate_phone?.trim() || undefined,
    gst_number: data.gstin?.trim(),
    pan_number: data.pan?.trim(),
    drug_license_number: data.drug_license_no?.trim() || undefined,
    drug_license_valid_until: data.license_valid_till || undefined,
    payment_terms: data.payment_terms?.trim(),
    credit_period_days: Number(data.credit_period) || undefined,
    rating: data.rating !== '' && data.rating != null ? String(data.rating) : undefined,
    bank_name: data.bank_name?.trim() || undefined,
    account_number: data.bank_account?.trim() || undefined,
    ifsc_code: data.bank_ifsc?.trim() || undefined,
    currency: data.currency,
    documents,
    vendor_payload: {},
  };

  if (!isEdit) {
    payload.vendor_code = data.vendor_code?.trim();
    return withStoreCreateBody(payload);
  }
  return withStoreUpdateBody(payload);
}

// Vendors — proxied via EMR (/app/api/inventory/...), ORG-CODE resolves org scope.
// API: GET /inventory/vendors/
export const getVendors = (p) => client.get('/inventory/vendors/', p);
// API: GET /inventory/vendors/{id}/
export const getVendor = (id) => client.get(`/inventory/vendors/${id}/`);
// API: POST /inventory/vendors/
export const createVendor = (body) => client.post('/inventory/vendors/', body);
// API: PATCH /inventory/vendors/{id}/
export const updateVendor = (id, body) => client.patch(`/inventory/vendors/${id}/`, body);

// Workflow — POST action endpoints (vendor-bidding-service)
export const submitVendorForApproval = (id, opts = {}) =>
  client.post(`/inventory/vendors/${id}/submit-for-approval/`, vendorActionBody(opts));
export const approveVendor = (id, opts = {}) =>
  client.post(`/inventory/vendors/${id}/approve/`, vendorActionBody(opts));
export const suspendVendor = (id, opts = {}) =>
  client.post(`/inventory/vendors/${id}/suspend/`, vendorActionBody(opts));
export const reactivateVendor = (id, opts = {}) =>
  client.post(`/inventory/vendors/${id}/reactivate/`, vendorActionBody(opts));
export const blacklistVendor = (id, opts = {}) =>
  client.post(`/inventory/vendors/${id}/blacklist/`, vendorActionBody(opts));
// Reject — no dedicated POST in spec; PATCH with rejection_reason
export const rejectVendor = (id, opts = {}) =>
  client.patch(`/inventory/vendors/${id}/`, withStoreUpdateBody({
    rejection_reason: opts.reason || opts.remarks || undefined,
  }));

// Vendor Items
export const getVendorItems = (p) => client.get('/inventory/vendor-items/', p);
export const createVendorItem = (body) => client.post('/inventory/vendor-items/', body);

// RFQs — see api/rfq.js (re-exported for backward compatibility)
export {
  getRfqs,
  getRfq,
  createRfq,
  updateRfq,
  getRfqItems,
  addRfqItem,
  getRfqItem,
  updateRfqItem,
  deleteRfqItem,
  syncRfqItems,
  getRfqSummary,
  publishRfq,
  closeRfq,
  cancelRfq,
  getRfqQuotes,
  getRfqQuoteComparison,
  getRfqEvaluationSummary,
  getRfqVendors,
  createRfqWithItemsAndVendors,
  updateRfqWithItemsAndVendors,
  getVendorQuotes,
  getVendorQuote,
  createVendorQuote,
  updateVendorQuote,
  addVendorQuoteItem,
  getVendorQuoteItemsByQuote,
  submitVendorQuote,
  submitQuote,
  withdrawVendorQuote,
  startQuoteEvaluation,
  evaluateVendorQuote,
  acceptVendorQuote,
  getVendorQuoteItems,
  createVendorQuoteItem,
  updateVendorQuoteItem,
  deleteVendorQuoteItem,
  getQuoteEvaluation,
  getAcceptedQuotes,
  createAcceptedQuote,
  updateAcceptedQuote,
  acceptQuote,
  markConvertedToPo,
} from './rfq';
